<template>
  <div class="row">
    <div class="col-xs-12">
      <div v-if="isTeamRegistrationRoster" class="ibox">
        <div class="ibox-content p-y-md text-center">
          <i class="fa-solid fa-circle-info text-blue-500 text-20 m-b-sm"></i>
          <h3 class="font-semi m-b-xs">Team Registration Roster</h3>
          <p class="text-gray-500">
            This roster is managed by Team Registration. Use the Manage Teams page to edit members and weight classes.
          </p>
        </div>
      </div>
      <div v-else class="ibox">
        <div class="ibox-content p-t-m">
          <!-- The native nav bar owns the title and Save in the app embed -->
          <div
            class="flex flex-wrap justify-space-between align-items-baseline"
            :class="forAppEmbed ? 'm-b-sm' : 'm-t-sm m-b-m'"
          >
            <div v-if="!forAppEmbed">
              <h1 class="m-y-none m-b-md font-heavy" v-if="editing">
                Edit {{ name }} roster
              </h1>
              <h1 class="m-y-none m-b-md font-heavy" v-else>Create a roster</h1>
            </div>
            <div>
              <ladda-button
                v-if="!forAppEmbed"
                @lbClicked="saveRoster"
                el-class="btn-primary"
                :loading="loading"
                data-cy="save-roster"
              >
                Save
              </ladda-button>
              <div>
                <small v-show="error"
                  ><span class="text-danger">{{ errorMessage }}</span></small
                >
              </div>
            </div>
          </div>
          <div v-if="loadingCurrentMembers">
            <spinner></spinner>
          </div>
          <div v-if="!loadingCurrentMembers">
            <hr v-if="!forAppEmbed" />
            <div class="row" :class="{ 'm-t-md': !forAppEmbed }">
              <div class="col-sm-12 col-md-4">
                <h2 class="form-section-title">Info & Options</h2>
                <p></p>
              </div>
              <div class="col-sm-12 col-md-8 m-t-only-xs-m">
                <div class="form-group">
                  <label for="roster_name">Name</label>
                  <input
                    v-model="name"
                    type="text"
                    id="roster_name"
                    class="form-control"
                    maxlength="200"
                    data-cy="roster-name"
                  />
                </div>
                <div>
                  <div>
                    <label>Tags</label>
                    <VDropdown theme="info-tooltip">
                      <!-- This will be the popover target (for the events and position) -->
                      <i class="fa-solid fa-question"></i>
                      <!-- This will be the content of the popover -->
                      <template v-slot:popper>
                        <div>
                          <p class="header">What are roster tags for?</p>
                          <p>
                            When a wrestler is part of a roster with tags,
                            WrestlingIQ will show a small colored dot, along
                            with the tag abbreviation, next to their name.
                          </p>
                          <p>
                            You could use this for putting a green dot next to
                            your 'Active Members' roster. Or for non-profit
                            clubs, to track youth development effort and impact.
                          </p>
                        </div>
                      </template>
                    </VDropdown>
                  </div>
                  <div class="grid grid-cols-2">
                    <div>
                      <p>Tags added</p>
                      <div class="flex flex-wrap gap-x-xs items-center">
                        <span
                          v-for="tagging in notDestroyedTaggings"
                          @click="tagSyncClicked(tagging.tag)"
                          class="inline-flex align-items-center gap-x-xxs rounded-xxs font-small p-x-xs p-y-xxs"
                          :class="tagging.tag.archived ? 'opacity-50' : ''"
                          :style="{
                            color: tagging.tag.text_color,
                            'background-color': tagging.tag.color,
                          }"
                        >
                          {{ tagging.tag.abbreviation
                          }}<span v-if="tagging.tag.archived"> (archived)</span>
                          <i class="fa-solid fa-xmark"></i>
                        </span>
                      </div>
                    </div>
                    <div>
                      <p>Tags available</p>
                      <div class="flex flex-wrap gap-x-xs items-center">
                        <span
                          v-for="tag in notAddedTags"
                          @click="tagSyncClicked(tag)"
                          class="inline-flex align-items-center gap-x-xxs rounded-xxs font-small p-x-xs p-y-xxs cursor-pointer"
                          :style="{
                            color: tag.text_color,
                            'background-color': tag.color,
                          }"
                        >
                          {{ tag.abbreviation }}
                        </span>
                      </div>
                      <div class="flex flex-wrap flex-col-xs-only gap-xxs">
                        <button
                          class="btn btn-default m-t-sm"
                          @click="showAddTagForm = true"
                          v-if="!showAddTagForm"
                        >
                          Create new tag
                        </button>
                        <a
                          href="/tags"
                          v-if="!showAddTagForm && !forAppEmbed"
                          class="btn btn-default m-t-sm"
                        >
                          Manage tags
                        </a>
                      </div>
                    </div>
                  </div>
                  <div class="m-t-sm">
                    <add-tag-form
                      v-if="showAddTagForm"
                      @hide="showAddTagForm = false"
                    ></add-tag-form>
                  </div>
                </div>
                <!-- Depth Chart / Division Selection -->
                <div class="m-t-lg">
                  <div class="form-group">
                    <div>
                      <label>Depth Chart</label>
                      <VDropdown theme="info-tooltip">
                        <i class="fa-solid fa-question"></i>
                        <template v-slot:popper>
                          <div>
                            <p class="header">What is a depth chart?</p>
                            <p>
                              A depth chart lets you organize wrestlers by
                              weight class and set their positions (starter,
                              backup, etc.).
                            </p>
                            <p>
                              Select an age division to enable the depth chart
                              feature. Wrestlers can then be assigned to
                              specific weight classes with position ordering.
                            </p>
                          </div>
                        </template>
                      </VDropdown>
                    </div>
                    <div v-if="loadingDivisionStandards" class="p-sm">
                      <spinner></spinner>
                    </div>
                    <div v-else>
                      <select
                        v-model="selectedDivisionStandardId"
                        class="form-control"
                        @change="onDivisionStandardChange"
                        data-cy="division-standard-select"
                      >
                        <option :value="null">No depth chart</option>
                        <option
                          v-for="standard in divisionStandards"
                          :key="standard.id"
                          :value="standard.id"
                        >
                          {{ standard.name }}
                        </option>
                      </select>
                      <p v-if="!forAppEmbed" class="text-gray-600 text-12 m-t-xs">
                        <a
                          href="/settings/team/division_standards"
                          class="text-underline"
                        >
                          Create custom divisions
                          <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                        </a>
                      </p>
                      <div v-if="selectedDivisionStandard" class="m-t-sm">
                        <label>Age Division</label>
                        <select
                          v-model="selectedAgeDivisionId"
                          class="form-control"
                          @change="onAgeDivisionChange"
                          data-cy="age-division-select"
                        >
                          <option :value="null">Select age division...</option>
                          <option
                            v-for="division in selectedDivisionStandard.age_divisions"
                            :key="division.id"
                            :value="division.id"
                          >
                            {{ division.display_name }}
                            <span v-if="division.eligibility_summary">
                              ({{ division.eligibility_summary }})
                            </span>
                          </option>
                        </select>
                        <p
                          v-if="selectedAgeDivision"
                          class="text-gray-600 text-12 m-t-xs"
                        >
                          {{ selectedAgeDivision.eligibility_summary }}
                          <span v-if="availableWeightClasses.length > 0">
                            - {{ availableWeightClasses.length }} weight classes
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Location -->
                <div class="m-t-lg" v-if="!loadingLocations && locations.length > 0">
                  <div class="form-group">
                    <label>Location</label>
                    <div>
                      <select
                        v-model="selectedLocationId"
                        class="form-control"
                        @change="unsavedChanges = true"
                        data-cy="location-select"
                      >
                        <option :value="null">No location</option>
                        <option
                          v-for="location in selectableLocations"
                          :key="location.id"
                          :value="location.id"
                        >
                          {{ location.name }}<span v-if="location.archived"> (archived)</span>
                        </option>
                      </select>
                      <p v-if="!forAppEmbed" class="text-gray-600 text-12 m-t-xs">
                        <a
                          href="/settings/team/locations"
                          class="text-underline"
                        >
                          Manage locations
                          <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                <!-- Weight tracking opt-out (only when the team has weight tracking enabled) -->
                <div class="m-t-lg" v-if="showWeightTracking">
                  <div class="form-group">
                    <label class="font-normal flex align-items-baseline gap-x-xs">
                      <input
                        v-model="weightTrackingDisabled"
                        type="checkbox"
                        data-cy="weight-tracking-disabled"
                        @change="unsavedChanges = true"
                      >
                      <span>
                        <span class="font-semi block">Disable weight tracking for this roster</span>
                        <small class="text-gray-600">
                          Wrestlers whose active rosters all have weight tracking disabled won't be asked to log their weight at the kiosk.
                        </small>
                      </span>
                    </label>
                  </div>
                </div>
                <div v-if="showSyncers && !showReSyncForm" class="m-t-lg">
                  <div class="form-group">
                    <div
                      class="flex justify-space-between align-items-baseline"
                    >
                      <div>
                        <label>Sync roster with registrations?</label>
                        <VDropdown theme="info-tooltip">
                          <!-- This will be the popover target (for the events and position) -->
                          <i class="fa-solid fa-question"></i>
                          <!-- This will be the content of the popover -->
                          <template v-slot:popper>
                            <div>
                              <p class="header">
                                What does syncing a roster with a session do?
                              </p>
                              <p>
                                For registrations with a single payment,
                                wrestlers will get added to this roster as soon
                                as their registration has been paid for.
                              </p>
                              <p>
                                For recurring memberships, or installments,
                                wrestlers will get added to this roster when
                                their membership is in good standing and removed
                                from it when their membership is cancelled, or
                                payment fails.
                              </p>
                              <p>
                                For drop ins, wrestlers will get added when they
                                receive > 0 passes and are removed when they no
                                longer have passes.
                              </p>
                            </div>
                          </template>
                        </VDropdown>
                      </div>
                      <div v-if="editing">
                        <a
                          @click.prevent="showReSyncForm = true"
                          class="btn btn-default btn-compact flex gap-x-xs justify-center align-items-center"
                        >
                          <i class="fa-solid fa-arrows-rotate"></i>
                          Resync
                        </a>
                      </div>
                    </div>
                    <div class="m-t-sm">
                      <search
                        class="search-form"
                        response-key="paid_sessions"
                        placeholder="Type name of paid session..."
                        :mutate-history="false"
                        :allow-add="false"
                        :small-input="true"
                        :display-all-on-empty="false"
                        readable-model-name="session"
                        base-url="/api/v1/paid_sessions?type=not_archived"
                      >
                        <template v-slot:list="slotProps">
                          <div
                            class="border-top m-t-sm m-b-xs p-t-sm p-b-xs cursor-pointer flex justify-space-between align-items-center p-r-sm"
                            @click="sessionSyncClicked(slotProps.result)"
                          >
                            <div>
                              {{ slotProps.result.name }}
                            </div>
                            <div
                              class="border rounded-sm border-blue-200 border-solid font-small"
                              style="padding: 2px 6px"
                            >
                              Sync
                            </div>
                          </div>
                        </template>
                      </search>
                      <div class="flex flex-wrap gap-x-xxs gap-y-xxs m-t-sm">
                        <button
                          @click="sessionSyncClicked(sync.paid_session)"
                          class="btn btn-success btn-outline rounded-lg font-small"
                          style="padding: 2px 6px"
                          v-for="sync in notDestroyedSyncers"
                        >
                          {{ sync.paid_session.name }}
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                      <div
                        class="m-t-xs"
                        v-if="
                          notDestroyedSyncers.length > 0 ||
                          destroyedSyncers.length > 0
                        "
                      >
                        <span v-if="notDestroyedSyncers.length > 0">
                          <strong>Note:</strong> syncing of wrestlers based on
                          registrations does not happen until after you press
                          save.<br />
                        </span>
                        <span v-if="destroyedSyncers.length > 0">
                          <strong>Note:</strong> When removing a registration,
                          the wrestlers part of that registration will be
                          removed unless they are part of another synced
                          registration.<br />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="showReSyncForm" class="m-t-lg">
                  <h3>Are you sure you want to resync?</h3>
                  <p class="text-heavy">
                    Resyncing will remove all wrestlers from this roster, then
                    re-add the wrestlers who have good standing registrations
                    that are synced to this roster.
                  </p>
                  <p class="text-heavy">
                    If you have manually added wrestlers to this roster, they
                    will be removed.
                  </p>
                  <div class="flex gap-x-xs">
                    <a
                      class="btn btn-default"
                      @click.prevent="showReSyncForm = false"
                    >
                      Cancel
                    </a>
                    <ladda-button
                      @lbClicked="triggerSync"
                      el-class="btn-primary"
                      :loading="reSyncingRoster"
                    >
                      Yes, re-sync roster
                    </ladda-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Wrestlers Section -->
            <div class="row m-t-md">
              <div class="col-sm-12 col-md-4">
                <h2 class="form-section-title">Wrestlers</h2>
                <p>
                  Manage which wrestlers are on this roster.
                </p>
              </div>
              <div class="col-sm-12 col-md-8">
                <!-- Tab buttons -->
                <div class="flex gap-x-xs m-b-sm">
                  <button
                    class="btn btn-sm"
                    :class="wrestlerViewMode === 'roster' ? 'btn-primary' : 'btn-default'"
                    @click="wrestlerViewMode = 'roster'"
                    data-cy="tab-on-roster"
                  >
                    On Roster ({{ displayedMemberCount }})
                  </button>
                  <button
                    class="btn btn-sm"
                    :class="wrestlerViewMode === 'all' ? 'btn-primary' : 'btn-default'"
                    @click="wrestlerViewMode = 'all'"
                    data-cy="tab-add-wrestlers"
                  >
                    <i class="fa-solid fa-plus m-r-xxs"></i> Add Wrestlers
                  </button>
                </div>

                <!-- ON ROSTER / DEPTH CHART VIEW (default) -->
                <div v-if="wrestlerViewMode === 'roster'" class="form-group">
                  <!-- DEPTH CHART VIEW (when age division selected) -->
                  <div v-if="depthChartEnabled">
                    <p class="text-gray-500 text-12 m-t-xs m-b-sm">
                      Drag wrestlers between weight classes to assign them.
                      Order within each weight class determines position
                      (starter, backup, etc.).
                    </p>
                    <div
                      v-if="allActiveMembers.length === 0"
                      class="text-gray-500 m-t-sm p-md text-center"
                    >
                      No wrestlers on this roster yet.
                      <a href="#" @click.prevent="wrestlerViewMode = 'all'" class="text-underline">Add wrestlers</a>
                    </div>
                    <div v-else class="m-t-sm">
                      <!-- Search input for depth chart -->
                      <div class="m-b-sm">
                        <input
                          type="text"
                          v-model="memberSearchQuery"
                          placeholder="Search current wrestlers..."
                          class="form-control"
                          @click.stop
                        />
                        <div
                          v-if="
                            memberSearchQuery &&
                            searchMatchLocations.length > 0
                          "
                          class="m-t-xs"
                        >
                          <span class="text-gray-500 text-12"
                            >Found in:
                          </span>
                          <span
                            v-for="(location, index) in searchMatchLocations"
                            :key="location.id"
                          >
                            <a
                              href="#"
                              class="text-12"
                              @click.prevent="jumpToSection(location.id)"
                              >{{ location.name }} ({{ location.count }})</a
                            ><span
                              v-if="index < searchMatchLocations.length - 1"
                              class="text-gray-400"
                              >,
                            </span>
                          </span>
                        </div>
                        <p
                          v-if="
                            memberSearchQuery &&
                            searchMatchLocations.length === 0
                          "
                          class="text-gray-500 text-12 m-t-xs"
                        >
                          No wrestlers match "{{ memberSearchQuery }}"
                        </p>
                      </div>
                      <!-- Expand/Collapse All -->
                      <div class="flex justify-end gap-x-sm m-b-xs">
                        <a
                          href="#"
                          class="text-12"
                          @click.prevent="expandAllSections"
                        >
                          Expand all
                        </a>
                        <span class="text-gray-300">|</span>
                        <a
                          href="#"
                          class="text-12"
                          @click.prevent="collapseAllSections"
                        >
                          Collapse all
                        </a>
                      </div>
                      <!-- Unassigned Section -->
                      <div
                        class="m-b-sm border border-solid border-gray-200 rounded-sm"
                      >
                        <div
                          class="flex justify-space-between align-items-center p-sm bg-gray-100 cursor-pointer"
                          :class="
                            unassignedExpanded ? 'rounded-t-sm' : 'rounded-sm'
                          "
                          @click="unassignedExpanded = !unassignedExpanded"
                        >
                          <div class="flex align-items-center gap-x-xs">
                            <i
                              :class="
                                unassignedExpanded
                                  ? 'fa-solid fa-chevron-down'
                                  : 'fa-solid fa-chevron-right'
                              "
                            ></i>
                            <span class="font-semi">Unassigned</span>
                            <span class="text-gray-500 text-12">
                              ({{ unassignedWrestlers.length }})
                            </span>
                          </div>
                        </div>
                        <div
                          v-show="unassignedExpanded"
                          class="p-sm"
                          ref="unassigned-section"
                        >
                          <draggable
                            :value="unassignedWrestlers"
                            @input="
                              (newList) => handleUnassignedDrop(newList)
                            "
                            group="depth-chart"
                            ghost-class="bg-blue-50"
                            :empty-insert-threshold="50"
                          >
                            <div
                              v-for="member in unassignedWrestlers"
                              :key="memberKey(member)"
                              class="m-b-xs"
                              :class="{
                                'opacity-25':
                                  memberSearchQuery &&
                                  !memberMatchesSearch(member),
                              }"
                            >
                              <div
                                class="grid grid-cols-2 sm-grid-cols-1 md-grid-cols-1 justify-space-between align-items-center p-sm rounded-sm gap-sm cursor-move"
                                :class="getMemberRowClasses(member)"
                              >
                                <div class="flex align-items-center gap-x-sm">
                                  <!-- Avatar -->
                                  <div
                                    class="bg-gray-100 rounded-full flex-shrink-0 flex align-items-center"
                                    style="padding: 2px"
                                  >
                                    <img
                                      v-if="member.profile.avatar_thumbnail"
                                      :src="member.profile.avatar_thumbnail"
                                      :alt="member.profile.full_name"
                                      class="rounded-full"
                                      style="
                                        width: 32px;
                                        height: 32px;
                                        object-fit: cover;
                                      "
                                    />
                                    <svg
                                      v-else
                                      class="text-gray-400"
                                      style="width: 32px; height: 32px"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                                      />
                                    </svg>
                                  </div>
                                  <!-- Name and details -->
                                  <div>
                                    <div
                                      class="flex align-items-center gap-x-xs"
                                    >
                                      <span class="font-semi text-gray-900">
                                        {{ member.profile.full_name }}
                                      </span>
                                      <span
                                        v-if="member._isPending"
                                        class="bg-blue-100 text-blue-600 text-12 p-x-xs rounded-xxs"
                                      >
                                        NEW
                                      </span>
                                      <span
                                        v-if="!isWrestlerEligible(member)"
                                        class="text-yellow-600 text-12"
                                      >
                                        <i
                                          class="fa-solid fa-triangle-exclamation m-r-xxs"
                                        ></i>
                                        {{
                                          getWrestlerIneligibilityReason(
                                            member
                                          )
                                        }}
                                      </span>
                                    </div>
                                    <div class="text-12 text-gray-500">
                                      <span v-if="member.profile.weight_class"
                                        >{{
                                          member.profile.weight_class
                                        }}
                                        lbs</span
                                      >
                                      <span
                                        v-if="
                                          member.profile.weight_class &&
                                          member.profile.dob
                                        "
                                      >
                                        /
                                      </span>
                                      <span v-if="member.profile.dob"
                                        >Age
                                        {{
                                          calculateAge(member.profile.dob)
                                        }}</span
                                      >
                                      <span
                                        v-if="
                                          member.profile.gender &&
                                          (member.profile.weight_class ||
                                            member.profile.dob)
                                        "
                                      >
                                        /
                                      </span>
                                      <span v-if="member.profile.gender">{{
                                        member.profile.gender
                                      }}</span>
                                      <button
                                        v-if="
                                          editingDetailsForMember !==
                                          member.profile.id
                                        "
                                        type="button"
                                        class="btn btn-link btn-xs p-none"
                                        @click.stop="
                                          editingDetailsForMember =
                                            member.profile.id
                                        "
                                        title="Edit wrestler details"
                                      >
                                        <i
                                          class="fa-solid fa-pencil text-gray-400"
                                        ></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  class="flex align-items-center gap-xs flex-col-xs-only"
                                >
                                  <select
                                    class="form-control w-full-xs-only"
                                    :value="member.weight_class_id || ''"
                                    @change="
                                      handleWeightClassChange(
                                        member,
                                        $event.target.value
                                      )
                                    "
                                    @click.stop
                                  >
                                    <option value="">
                                      Set weight class...
                                    </option>
                                    <option
                                      v-for="wc in availableWeightClasses"
                                      :key="wc.id"
                                      :value="wc.id"
                                    >
                                      {{ formatWeightClassName(wc) }}
                                    </option>
                                  </select>
                                  <button
                                    @click.stop="
                                      member._isPending
                                        ? removePendingAddition(
                                            member.profile.id
                                          )
                                        : toggleMemberRemoval(member)
                                    "
                                    class="btn btn-danger-light w-full-xs-only btn-xs"
                                  >
                                    <i class="fa-solid fa-xmark"></i>
                                    <span>Remove from roster</span>
                                  </button>
                                </div>
                              </div>
                              <!-- Inline edit form -->
                              <roster-wrestler-inline-edit
                                v-if="
                                  editingDetailsForMember ===
                                  member.profile.id
                                "
                                :member="member"
                                class="m-t-xs"
                                @saved="handleWrestlerDetailsSaved"
                                @cancel="editingDetailsForMember = null"
                              />
                            </div>
                          </draggable>
                          <div
                            v-if="unassignedWrestlers.length === 0"
                            class="text-gray-500 text-12 p-sm text-center"
                          >
                            All wrestlers assigned to weight classes
                          </div>
                        </div>
                      </div>

                      <!-- Weight Class Sections -->
                      <div
                        v-for="wc in availableWeightClasses"
                        :key="wc.id"
                        class="m-b-sm border border-solid border-gray-200 rounded-sm"
                      >
                        <div
                          class="flex justify-space-between align-items-center p-sm bg-gray-50 cursor-pointer"
                          :class="
                            expandedWeightClasses[wc.id]
                              ? 'rounded-t-sm'
                              : 'rounded-sm'
                          "
                          @click="toggleWeightClassExpanded(wc.id)"
                        >
                          <div class="flex align-items-center gap-x-xs">
                            <i
                              :class="
                                expandedWeightClasses[wc.id]
                                  ? 'fa-solid fa-chevron-down'
                                  : 'fa-solid fa-chevron-right'
                              "
                            ></i>
                            <span class="font-semi">{{
                              formatWeightClassName(wc)
                            }}</span>
                            <span class="text-gray-500 text-12">
                              ({{ getWeightClassCount(wc.id) }})
                            </span>
                          </div>
                        </div>
                        <div
                          v-show="expandedWeightClasses[wc.id]"
                          class="p-sm"
                          :ref="`wc-section-${wc.id}`"
                        >
                          <draggable
                            :value="wrestlersByWeightClass[wc.id]"
                            @input="
                              (newOrder) =>
                                handleWeightClassDrop(wc.id, newOrder)
                            "
                            group="depth-chart"
                            ghost-class="bg-blue-50"
                            :empty-insert-threshold="50"
                          >
                            <div
                              v-for="member in wrestlersByWeightClass[wc.id]"
                              :key="memberKey(member)"
                              class="m-b-xs"
                              :class="{
                                'opacity-25':
                                  memberSearchQuery &&
                                  !memberMatchesSearch(member),
                              }"
                            >
                              <div
                                class="grid grid-cols-1 justify-space-between align-items-center p-sm rounded-sm cursor-move"
                                :class="getMemberRowClasses(member)"
                              >
                                <div class="flex align-items-center gap-x-sm">
                                  <!-- Avatar -->
                                  <div
                                    class="bg-gray-100 rounded-full flex-shrink-0 flex align-items-center"
                                    style="padding: 2px"
                                  >
                                    <img
                                      v-if="member.profile.avatar_thumbnail"
                                      :src="member.profile.avatar_thumbnail"
                                      :alt="member.profile.full_name"
                                      class="rounded-full"
                                      style="
                                        width: 32px;
                                        height: 32px;
                                        object-fit: cover;
                                      "
                                    />
                                    <svg
                                      v-else
                                      class="text-gray-400"
                                      style="width: 32px; height: 32px"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                                      />
                                    </svg>
                                  </div>
                                  <!-- Name and details -->
                                  <div>
                                    <div
                                      class="flex align-items-center gap-x-xs"
                                    >
                                      <span class="font-semi text-gray-900">
                                        {{ member.profile.full_name }}
                                      </span>
                                      <span
                                        v-if="member._isPending"
                                        class="bg-blue-100 text-blue-600 text-12 p-x-xs rounded-xxs"
                                      >
                                        NEW
                                      </span>
                                      <span
                                        v-if="!isWrestlerEligible(member)"
                                        class="text-yellow-600 text-12"
                                      >
                                        <i
                                          class="fa-solid fa-triangle-exclamation m-r-xxs"
                                        ></i>
                                        {{
                                          getWrestlerIneligibilityReason(
                                            member
                                          )
                                        }}
                                      </span>
                                    </div>
                                    <div class="text-12 text-gray-500">
                                      <span v-if="member.profile.weight_class"
                                        >{{
                                          member.profile.weight_class
                                        }}
                                        lbs</span
                                      >
                                      <span
                                        v-if="
                                          member.profile.weight_class &&
                                          member.profile.dob
                                        "
                                      >
                                        /
                                      </span>
                                      <span v-if="member.profile.dob"
                                        >Age
                                        {{
                                          calculateAge(member.profile.dob)
                                        }}</span
                                      >
                                      <span
                                        v-if="
                                          member.profile.gender &&
                                          (member.profile.weight_class ||
                                            member.profile.dob)
                                        "
                                      >
                                        /
                                      </span>
                                      <span v-if="member.profile.gender">{{
                                        member.profile.gender
                                      }}</span>
                                      <button
                                        v-if="
                                          editingDetailsForMember !==
                                          member.profile.id
                                        "
                                        type="button"
                                        class="btn btn-link btn-xs p-none m-l-xs"
                                        @click.stop="
                                          editingDetailsForMember =
                                            member.profile.id
                                        "
                                        title="Edit wrestler details"
                                      >
                                        <i
                                          class="fa-solid fa-pencil text-gray-400"
                                        ></i>
                                      </button>
                                    </div>
                                    <div
                                      class="flex align-items-center gap-xs"
                                    >
                                      <!-- Show dropdown when editing this member -->
                                      <select
                                        v-if="
                                          editingWeightClassForMember ===
                                          member.profile.id
                                        "
                                        class="form-control"
                                        style="width: auto"
                                        :value="member.weight_class_id || ''"
                                        @change="
                                          handleWeightClassChangeAndClose(
                                            member,
                                            $event.target.value
                                          )
                                        "
                                        @blur="
                                          editingWeightClassForMember = null
                                        "
                                        @click.stop
                                      >
                                        <option value="">Unassign</option>
                                        <option
                                          v-for="wcOption in availableWeightClasses"
                                          :key="wcOption.id"
                                          :value="wcOption.id"
                                        >
                                          {{
                                            formatWeightClassName(wcOption)
                                          }}
                                        </option>
                                      </select>
                                      <!-- Show Change button when not editing -->
                                      <button
                                        v-else
                                        @click.stop="
                                          editingWeightClassForMember =
                                            member.profile.id
                                        "
                                        class="btn btn-default btn-xs m-t-xxs"
                                        title="Change weight class"
                                      >
                                        <i class="fa-regular fa-pen-to-square m-r-xxs"></i>
                                        Weight class
                                      </button>
                                      <button
                                        v-if="
                                          editingWeightClassForMember !==
                                          member.profile.id
                                        "
                                        @click.stop="
                                          member._isPending
                                            ? removePendingAddition(
                                                member.profile.id
                                              )
                                            : toggleMemberRemoval(member)
                                        "
                                        class="btn btn-danger-light btn-xs m-t-xxs"
                                        title="Remove from roster"
                                      >
                                        <i class="fa-solid fa-xmark m-r-xxs"></i>
                                        Remove from roster
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <!-- Inline edit form -->
                              <roster-wrestler-inline-edit
                                v-if="
                                  editingDetailsForMember ===
                                  member.profile.id
                                "
                                :member="member"
                                class="m-t-xs"
                                @saved="handleWrestlerDetailsSaved"
                                @cancel="editingDetailsForMember = null"
                              />
                            </div>
                          </draggable>
                          <div
                            v-if="getWeightClassCount(wc.id) === 0"
                            class="text-gray-400 text-12 p-sm text-center border border-dashed border-gray-300 rounded-sm"
                          >
                            Drag wrestlers here
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- FLAT ROSTER LIST (when no depth chart) -->
                  <div v-else-if="rosterMembers.length === 0 && !rosterSearchQuery" class="text-gray-500 p-md text-center">
                    No wrestlers on this roster yet.
                    <a href="#" @click.prevent="wrestlerViewMode = 'all'" class="text-underline">Add wrestlers</a>
                  </div>
                  <div v-else>
                    <input
                      type="text"
                      v-model="rosterSearchQuery"
                      placeholder="Search roster..."
                      class="form-control m-b-sm"
                    />
                    <div
                      v-if="filteredRosterMembers.length === 0 && rosterSearchQuery"
                      class="text-gray-500 p-md text-center"
                    >
                      No wrestlers match "{{ rosterSearchQuery }}"
                    </div>
                    <div
                      v-for="member in filteredRosterMembers"
                      :key="memberKey(member)"
                      class="m-b-xs"
                    >
                      <div
                        class="flex justify-space-between align-items-center p-sm rounded-sm border border-solid"
                        :class="getRosterViewRowClasses(member)"
                      >
                        <div class="flex align-items-center gap-x-sm">
                          <!-- Avatar -->
                          <div
                            class="bg-gray-100 rounded-full flex-shrink-0 flex align-items-center"
                            style="padding: 2px"
                          >
                            <img
                              v-if="member.profile.avatar_thumbnail"
                              :src="member.profile.avatar_thumbnail"
                              :alt="member.profile.full_name"
                              class="rounded-full"
                              style="
                                width: 32px;
                                height: 32px;
                                object-fit: cover;
                              "
                            />
                            <svg
                              v-else
                              class="text-gray-400"
                              style="width: 32px; height: 32px"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                              />
                            </svg>
                          </div>
                          <!-- Name and details -->
                          <div>
                            <div class="flex align-items-center gap-x-xs">
                              <span
                                class="font-semi"
                                :class="member._destroy ? 'text-gray-400' : 'text-gray-900'"
                                :style="member._destroy ? 'text-decoration: line-through;' : ''"
                              >
                                {{ member.profile.full_name }}
                              </span>
                              <span
                                v-if="member._isPending"
                                class="bg-blue-100 text-blue-600 text-12 p-x-xs rounded-xxs"
                              >
                                NEW
                              </span>
                              <span
                                v-if="member.profile.profile_type === 'guest'"
                                class="bg-gray-200 text-gray-600 text-12 p-x-xs rounded-xxs"
                              >
                                Guest
                              </span>
                            </div>
                            <div
                              class="text-12"
                              :class="member._destroy ? 'text-gray-300' : 'text-gray-500'"
                            >
                              <span v-if="member.profile.weight_class"
                                >{{ member.profile.weight_class }} lbs</span
                              >
                              <span
                                v-if="member.profile.weight_class && member.profile.dob"
                              >
                                /
                              </span>
                              <span v-if="member.profile.dob"
                                >Age {{ calculateAge(member.profile.dob) }},
                                Born {{ getBirthYear(member.profile.dob) }}</span
                              >
                              <span
                                v-if="member.profile.gender && (member.profile.weight_class || member.profile.dob)"
                              >
                                /
                              </span>
                              <span v-if="member.profile.gender">{{ member.profile.gender }}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <button
                            v-if="member._destroy"
                            @click="undoRemoveByProfileId(member.profile.id)"
                            class="btn btn-success btn-xs"
                          >
                            <i class="fa-solid fa-arrow-rotate-left m-r-xxs"></i> Undo
                          </button>
                          <button
                            v-else
                            @click="removeFromRoster(member.profile.id)"
                            class="btn btn-default btn-xs"
                          >
                            <i class="fa-solid fa-xmark m-r-xxs"></i> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ADD WRESTLERS VIEW -->
                <div v-if="wrestlerViewMode === 'all'" class="form-group">
                  <filtered-list
                    ui="grid"
                    grid-classes="m-t-sm"
                    :headers="[]"
                    :show-primary-filters="false"
                    :show-sort="true"
                    :show-search-bar="true"
                    search-bar-placeholder="Search wrestlers by name..."
                    search-bar-attribute="full_name"
                    :filters="wrestlerFilters"
                    :sorts="[
                      { label: 'Last name A-Z', value: 'last_name asc' },
                      { label: 'Last name Z-A', value: 'last_name desc' },
                      { label: 'First name A-Z', value: 'first_name asc' },
                      { label: 'First name Z-A', value: 'first_name desc' },
                      {
                        label: 'Weight (lightest)',
                        value: 'weight_class_numeric asc',
                      },
                      {
                        label: 'Weight (heaviest)',
                        value: 'weight_class_numeric desc',
                      },
                      { label: 'Age (youngest)', value: 'dob desc' },
                      { label: 'Age (oldest)', value: 'dob asc' },
                    ]"
                    response-key="wrestlers"
                    object-type="WrestlerProfile"
                    base-url="/api/v1/wrestlers?expand_rosters=true"
                  >
                    <template v-slot:row="row">
                      <div
                        class="flex justify-space-between align-items-center p-sm m-b-xs rounded-sm border border-solid cursor-pointer"
                        :class="getUnifiedRowClasses(row.data)"
                        @click="handleUnifiedRowClick(row.data)"
                      >
                        <div class="flex align-items-center gap-x-sm">
                          <!-- Avatar -->
                          <div
                            class="bg-gray-100 rounded-full flex-shrink-0 flex align-items-center"
                            style="padding: 2px"
                          >
                            <img
                              v-if="row.data.avatar_thumbnail"
                              :src="row.data.avatar_thumbnail"
                              :alt="row.data.full_name"
                              class="rounded-full"
                              style="
                                width: 32px;
                                height: 32px;
                                object-fit: cover;
                              "
                            />
                            <svg
                              v-else
                              class="text-gray-400"
                              style="width: 32px; height: 32px"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                              />
                            </svg>
                          </div>
                          <!-- Name and details -->
                          <div>
                            <div class="flex align-items-center gap-x-xs">
                              <span
                                class="font-semi"
                                :class="getWrestlerRosterState(row.data.id) === 'removing' ? 'text-gray-400' : 'text-gray-900'"
                                :style="getWrestlerRosterState(row.data.id) === 'removing' ? 'text-decoration: line-through;' : ''"
                              >
                                {{ row.data.full_name }}
                              </span>
                              <span
                                v-if="getWrestlerRosterState(row.data.id) === 'pending'"
                                class="bg-blue-100 text-blue-600 text-12 p-x-xs rounded-xxs"
                              >
                                NEW
                              </span>
                              <span
                                v-if="row.data.profile_type === 'guest'"
                                class="bg-gray-200 text-gray-600 text-12 p-x-xs rounded-xxs"
                              >
                                Guest
                              </span>
                            </div>
                            <div class="text-12 text-gray-500">
                              <span v-if="row.data.weight_class"
                                >{{ row.data.weight_class }} lbs</span
                              >
                              <span
                                v-if="row.data.weight_class && row.data.dob"
                              >
                                /
                              </span>
                              <span v-if="row.data.dob"
                                >Age {{ calculateAge(row.data.dob) }}, Born
                                {{ getBirthYear(row.data.dob) }}</span
                              >
                              <span
                                v-if="row.data.academic_class"
                                class="m-l-xs text-gray-400"
                                >({{ row.data.academic_class }})</span
                              >
                            </div>
                          </div>
                        </div>
                        <div class="flex align-items-center gap-x-xs">
                          <!-- Weight class assignment (depth chart only, when on roster) -->
                          <select
                            v-if="depthChartEnabled && (getWrestlerRosterState(row.data.id) === 'active' || getWrestlerRosterState(row.data.id) === 'pending')"
                            class="form-control input-sm"
                            style="width: auto"
                            :value="getMemberWeightClassId(row.data.id)"
                            @change="handleUnifiedWeightClassChange(row.data.id, $event.target.value)"
                            @click.stop
                          >
                            <option value="">Unassigned</option>
                            <option
                              v-for="wc in availableWeightClasses"
                              :key="wc.id"
                              :value="wc.id"
                            >
                              {{ formatWeightClassName(wc) }}
                            </option>
                          </select>
                          <!-- State-dependent action button -->
                          <button
                            v-if="getWrestlerRosterState(row.data.id) === 'not_in_roster'"
                            @click.stop="addWrestler(row.data)"
                            class="btn btn-primary btn-xs"
                          >
                            <i class="fa-solid fa-plus m-r-xxs"></i> Add
                          </button>
                          <button
                            v-else-if="getWrestlerRosterState(row.data.id) === 'removing'"
                            @click.stop="undoRemoveByProfileId(row.data.id)"
                            class="btn btn-success btn-xs"
                          >
                            <i class="fa-solid fa-arrow-rotate-left m-r-xxs"></i> Undo
                          </button>
                          <button
                            v-else
                            @click.stop="removeFromRoster(row.data.id)"
                            class="btn btn-default btn-xs"
                          >
                            <i class="fa-solid fa-check text-green-600 m-r-xxs"></i> On roster
                          </button>
                        </div>
                      </div>
                    </template>
                    <template v-slot:noresults>
                      <div class="text-center text-gray-500 p-md">
                        No wrestlers found. Try adjusting your filters.
                      </div>
                    </template>
                  </filtered-list>
                </div>
              </div>
            </div>

            <div class="row m-t-lg">
              <div class="col-xs-6">
                <a
                  class="text-danger"
                  @click.prevent="confirmDestroy"
                  v-show="existingRoster"
                  >Delete roster...</a
                >
              </div>
              <div class="col-xs-6 text-right">
                <ladda-button
                  v-if="!forAppEmbed"
                  @lbClicked="saveRoster"
                  el-class="btn-primary"
                  :loading="loading"
                >
                  Save
                </ladda-button>
                <div>
                  <small v-show="error"
                    ><span class="text-danger">{{ errorMessage }}</span></small
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from 'sweetalert2';
import draggable from 'vuedraggable';
import Search from '../../shared/search.vue';
import FilteredList from './filtered_list.vue';
import GenericSearchResult from './generic-search-result.vue';
import Spinner from './spinner.vue';
import FullLoadList from './full_load_list.vue';
import AddTagForm from './add_tag_form.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import {
  isEligibleForDivision,
  getIneligibilityReason,
} from './utils/divisionEligibility';
import RosterWrestlerInlineEdit from './roster_wrestler_inline_edit.vue';
import { appEmbedMix } from './mixins/app_embed_mix';

export default {
  name: 'roster-form',
  mixins: [appEmbedMix],
  components: {
    LaddaButton,
    FullLoadList,
    Spinner,
    GenericSearchResult,
    Search,
    AddTagForm,
    FilteredList,
    draggable,
    RosterWrestlerInlineEdit,
  },
  props: {
    existingRoster: {
      type: Object,
      required: false,
    },
    showSyncers: {
      type: Boolean,
      default: false,
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
    showWeightTracking: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Current roster members (loaded from API on edit)
      // Keyed by profile_id for O(1) lookup
      currentMembers: {},

      // Pending additions (wrestlers to add, not yet saved)
      // Keyed by profile_id
      pendingAdditions: {},

      rosterSyncers: {},
      taggings: {},
      // Tags are all possible tags
      tags: [],
      loading: false,
      loadingCurrentMembers: false,
      unsavedChanges: false,
      error: false,
      errorMessage: '',
      name: '',
      editing: false,
      showAddTagForm: false,
      showReSyncForm: false,
      reSyncingRoster: false,

      // Member list filtering
      memberSearchQuery: '',
      rosterSearchQuery: '',
      wrestlerViewMode: 'roster',

      // Depth chart / division settings
      divisionStandards: [],
      selectedDivisionStandardId: null,
      selectedAgeDivisionId: null,
      // An existing roster's age division is only restored once division
      // standards load; until then (or if that fails) saves keep the saved one.
      ageDivisionRestored: false,
      loadingDivisionStandards: false,

      // Location settings
      locations: [],
      selectedLocationId: null,
      loadingLocations: false,

      // Weight tracking opt-out
      weightTrackingDisabled: false,

      // Track which weight class sections are expanded
      expandedWeightClasses: {},
      unassignedExpanded: true,

      // Track which wrestler's weight class is being edited (by profile id)
      editingWeightClassForMember: null,
      // Track which wrestler's details are being edited inline (by profile id)
      editingDetailsForMember: null,
    };
  },
  computed: {
    isTeamRegistrationRoster() {
      return this.existingRoster && this.existingRoster.team_registration === true;
    },
    // Count of members that will be on the roster after save (excluding removed)
    displayedMemberCount() {
      let count = 0;
      for (const member of Object.values(this.currentMembers)) {
        if (!member._destroy) {
          count++;
        }
      }
      count += Object.keys(this.pendingAdditions).length;
      return count;
    },
    // All roster members (current + pending) sorted by name for the "On Roster" tab
    rosterMembers() {
      const members = [];
      for (const member of Object.values(this.currentMembers)) {
        members.push({ ...member, _isPending: false });
      }
      for (const addition of Object.values(this.pendingAdditions)) {
        members.push({ ...addition, _isPending: true });
      }
      return members.sort((a, b) => {
        const nameA = (a.profile.full_name || '').toLowerCase();
        const nameB = (b.profile.full_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    },
    // Roster members filtered by search query
    filteredRosterMembers() {
      if (!this.rosterSearchQuery.trim()) return this.rosterMembers;
      const query = this.rosterSearchQuery.toLowerCase().trim();
      return this.rosterMembers.filter((member) => {
        const fullName = (member.profile.full_name || '').toLowerCase();
        return fullName.includes(query);
      });
    },
    // Total count before search filtering (for showing/hiding controls)
    totalMemberCount() {
      return (
        Object.keys(this.currentMembers).length
        + Object.keys(this.pendingAdditions).length
      );
    },
    // Set of profile IDs in current members + pending (for search deduplication)
    allMemberIds() {
      const ids = new Set();
      for (const profileId of Object.keys(this.currentMembers)) {
        const member = this.currentMembers[profileId];
        if (!member._destroy) {
          ids.add(parseInt(profileId));
        }
      }
      for (const profileId of Object.keys(this.pendingAdditions)) {
        ids.add(parseInt(profileId));
      }
      return ids;
    },
    wrestlerFilters() {
      return [
        {
          label: 'Wrestler Type',
          attribute: 'profile_type',
          type: 'primary',
          predicates: [
            {
              id: 0,
              label: 'Include Guests',
              user_editable: false,
              default: true,
              values: {
                profile_type_in: ['teammate', 'guest'],
              },
            },
          ],
        },
        {
          // Hidden filter - used by the search bar, not shown in secondary filters
          label: 'Name',
          attribute: 'full_name',
          type: 'search_bar_only',
          predicates: [
            {
              id: 2,
              label: 'Name contains',
              type: 'TextContains',
              user_editable: true,
              operator: 'or',
              placeholder: 'First or last name',
              values: {
                first_name_cont: null,
                last_name_cont: null,
              },
            },
          ],
        },
        {
          label: 'Weight',
          predicateLabel: 'Filter by weight range',
          attribute: 'weight_class_numeric',
          type: 'secondary',
          predicates: [
            {
              id: 3,
              label: 'Weight between',
              type: 'NumberBetween',
              user_editable: true,
              minPlaceholder: 'Min lbs',
              maxPlaceholder: 'Max lbs',
              values: {
                weight_class_numeric_gteq: null,
                weight_class_numeric_lteq: null,
              },
            },
          ],
        },
        {
          label: 'Grade',
          predicateLabel: 'Filter by grade',
          attribute: 'academic_class',
          type: 'secondary',
          predicates: [
            {
              id: 4,
              label: 'Grade is',
              type: 'SingleSelect',
              user_editable: true,
              predicate_key: 'academic_class_eq',
              options: [
                // Pre-K to 12
                { value: 'Pre-K', label: 'Pre-K' },
                { value: 'Kindergarten', label: 'Kindergarten' },
                { value: '1st grade', label: '1st grade' },
                { value: '2nd grade', label: '2nd grade' },
                { value: '3rd grade', label: '3rd grade' },
                { value: '4th grade', label: '4th grade' },
                { value: '5th grade', label: '5th grade' },
                { value: '6th grade', label: '6th grade' },
                { value: '7th grade', label: '7th grade' },
                { value: '8th grade', label: '8th grade' },
                { value: '9th grade', label: '9th grade' },
                { value: '10th grade', label: '10th grade' },
                { value: '11th grade', label: '11th grade' },
                { value: '12th grade', label: '12th grade' },
                // College
                { value: 'Freshmen', label: 'Freshmen' },
                { value: 'RS Freshmen', label: 'RS Freshmen' },
                { value: 'Sophomore', label: 'Sophomore' },
                { value: 'RS Sophomore', label: 'RS Sophomore' },
                { value: 'Junior', label: 'Junior' },
                { value: 'RS Junior', label: 'RS Junior' },
                { value: 'Senior', label: 'Senior' },
                { value: 'RS Senior', label: 'RS Senior' },
                { value: 'Grad Student', label: 'Grad Student' },
                // Other
                { value: 'other', label: 'Other' },
                { value: 'Class of 2022', label: 'Class of 2022' },
                { value: 'Class of 2023', label: 'Class of 2023' },
                { value: 'Class of 2024', label: 'Class of 2024' },
                { value: 'Class of 2025', label: 'Class of 2025' },
                { value: 'Class of 2026', label: 'Class of 2026' },
                { value: 'Class of 2027', label: 'Class of 2027' },
              ],
              values: {
                academic_class_eq: null,
              },
            },
          ],
        },
        {
          label: 'Age',
          predicateLabel: 'Filter by age or birth year',
          attribute: 'age',
          type: 'secondary',
          predicates: [
            {
              id: 5,
              label: 'Born in year',
              type: 'SingleSelect',
              user_editable: true,
              predicate_key: 'birth_year_eq',
              options: [
                { value: 2025, label: '2025' },
                { value: 2024, label: '2024' },
                { value: 2023, label: '2023' },
                { value: 2022, label: '2022' },
                { value: 2021, label: '2021' },
                { value: 2020, label: '2020' },
                { value: 2019, label: '2019' },
                { value: 2018, label: '2018' },
                { value: 2017, label: '2017' },
                { value: 2016, label: '2016' },
                { value: 2015, label: '2015' },
                { value: 2014, label: '2014' },
                { value: 2013, label: '2013' },
                { value: 2012, label: '2012' },
                { value: 2011, label: '2011' },
                { value: 2010, label: '2010' },
                { value: 2009, label: '2009' },
                { value: 2008, label: '2008' },
                { value: 2007, label: '2007' },
                { value: 2006, label: '2006' },
                { value: 2005, label: '2005' },
              ],
              values: {
                birth_year_eq: null,
              },
            },
            {
              id: 6,
              label: 'Age between',
              type: 'NumberBetween',
              user_editable: true,
              minPlaceholder: 'Min age',
              maxPlaceholder: 'Max age',
              values: {
                age_gteq: null,
                age_lteq: null,
              },
            },
          ],
        },
      ];
    },
    notDestroyedSyncers() {
      return _.flatMap(this.rosterSyncers).filter((rs) => !rs._destroy);
    },
    destroyedSyncers() {
      return _.flatMap(this.rosterSyncers).filter((rs) => rs._destroy);
    },
    // Taggings added or to be added
    notDestroyedTaggings() {
      return _.flatMap(this.taggings).filter((rs) => !rs._destroy);
    },
    // Taggings to be removed
    destroyedTaggings() {
      return _.flatMap(this.taggings).filter((rs) => rs._destroy);
    },
    // Tags to show to the user
    notAddedTags() {
      return _.filter(this.tags, (t) => this.taggings[t.id] == null && !t.archived);
    },
    // Archived locations stay selectable only when the roster is already assigned to one
    selectableLocations() {
      return this.locations.filter(
        (l) => !l.archived || l.id === this.selectedLocationId,
      );
    },
    // Depth chart computed properties
    selectedDivisionStandard() {
      if (!this.selectedDivisionStandardId) return null;
      return this.divisionStandards.find(
        (s) => s.id === this.selectedDivisionStandardId,
      );
    },
    selectedAgeDivision() {
      if (!this.selectedAgeDivisionId || !this.selectedDivisionStandard) return null;
      return this.selectedDivisionStandard.age_divisions.find(
        (d) => d.id === this.selectedAgeDivisionId,
      );
    },
    depthChartEnabled() {
      return this.selectedAgeDivisionId != null;
    },
    availableWeightClasses() {
      if (!this.selectedAgeDivision) return [];
      return this.selectedAgeDivision.weight_classes || [];
    },
    // All non-destroyed members for depth chart grouping
    allActiveMembers() {
      const members = [];
      for (const member of Object.values(this.currentMembers)) {
        if (!member._destroy) {
          members.push({ ...member, _isPending: false });
        }
      }
      for (const addition of Object.values(this.pendingAdditions)) {
        members.push({ ...addition, _isPending: true });
      }
      return members;
    },
    // Wrestlers not assigned to any weight class
    unassignedWrestlers() {
      return this.allActiveMembers.filter((m) => !m.weight_class_id);
    },
    // Group wrestlers by weight class, sorted by position
    wrestlersByWeightClass() {
      const grouped = {};
      for (const wc of this.availableWeightClasses) {
        grouped[wc.id] = this.allActiveMembers
          .filter((m) => m.weight_class_id === wc.id)
          .sort((a, b) => (a.position || 999) - (b.position || 999));
      }
      return grouped;
    },
    // Locations where search matches are found (for "Found in" links)
    searchMatchLocations() {
      if (!this.memberSearchQuery.trim()) return [];
      const locations = [];

      // Check unassigned
      const unassignedMatches = this.unassignedWrestlers.filter((m) => this.memberMatchesSearch(m));
      if (unassignedMatches.length > 0) {
        locations.push({
          id: 'unassigned',
          name: 'Unassigned',
          count: unassignedMatches.length,
        });
      }

      // Check each weight class
      for (const wc of this.availableWeightClasses) {
        const matches = (this.wrestlersByWeightClass[wc.id] || []).filter((m) => this.memberMatchesSearch(m));
        if (matches.length > 0) {
          locations.push({
            id: wc.id,
            name: this.formatWeightClassName(wc),
            count: matches.length,
          });
        }
      }

      return locations;
    },
  },
  watch: {
    unsavedChanges(hasChanges) {
      if (this.forAppEmbed) {
        this.setNativeExitConfirmation(hasChanges, 'Discard roster changes?');
        return;
      }
      if (hasChanges) {
        window.onbeforeunload = function () {
          return true;
        };
      } else {
        window.onbeforeunload = null;
      }
    },
    name() {
      if (!this.loadingCurrentMembers) {
        this.unsavedChanges = true;
      }
    },
  },
  created() {
    this.$notificationManager.on('tag-created', this.tagCreated);
    if (this.existingRoster) {
      this.name = this.existingRoster.name;
      this.editing = true;
      this.rosterSyncers = _.keyBy(
        _.map(this.existingRoster.roster_syncers, (rs) => {
          rs._destroy = false;
          return rs;
        }),
        'paid_session.id',
      );
      this.taggings = _.keyBy(
        _.map(this.existingRoster.taggings, (tagging) => {
          tagging._destroy = false;
          return tagging;
        }),
        'tag.id',
      );
      this.loadCurrentMembers();
    } else {
      // Create mode - no members to load, default to add view
      this.loadingCurrentMembers = false;
      this.wrestlerViewMode = 'all';
    }
    if (this.existingRoster && this.existingRoster.location_id) {
      this.selectedLocationId = this.existingRoster.location_id;
    }
    if (this.existingRoster) {
      this.weightTrackingDisabled = !!this.existingRoster.weight_tracking_disabled;
    }
    this.loadTags();
    this.fetchDivisionStandards();
    this.fetchLocations();
  },
  mounted() {
    // Team registration rosters render a read-only notice, nothing to save.
    if (!this.isTeamRegistrationRoster) {
      this.setupNativeSaveButton(this.saveRoster);
    }
  },
  destroyed() {
    this.$notificationManager.off('tag-created', this.tagCreated);
    window.onbeforeunload = null;
  },
  methods: {
    memberKey(member) {
      return `${member._isPending ? 'pending' : 'current'}-${
        member.profile.id
      }`;
    },
    getMemberRowClasses(member) {
      if (member._destroy) {
        return 'bg-gray-100 border border-solid border-gray-200';
      }
      if (member._isPending) {
        return 'bg-blue-50 border border-solid border-blue-200';
      }
      return 'bg-white border border-solid border-gray-200';
    },
    calculateAge(dob) {
      if (!dob) return null;
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0
        || (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age;
    },
    getBirthYear(dob) {
      if (!dob) return null;
      return new Date(dob).getFullYear();
    },
    isWrestlerInRoster(wrestlerId) {
      return this.allMemberIds.has(wrestlerId);
    },
    getWrestlerRosterState(wrestlerId) {
      if (this.pendingAdditions[wrestlerId]) {
        return 'pending';
      }
      const member = this.currentMembers[wrestlerId];
      if (member) {
        return member._destroy ? 'removing' : 'active';
      }
      return 'not_in_roster';
    },
    getRosterViewRowClasses(member) {
      if (member._destroy) {
        return 'bg-gray-100 border-gray-200';
      }
      if (member._isPending) {
        return 'bg-blue-50 border-blue-200';
      }
      return 'border-gray-200';
    },
    getUnifiedRowClasses(wrestler) {
      const state = this.getWrestlerRosterState(wrestler.id);
      switch (state) {
        case 'active':
          return 'bg-green-50 border-green-200';
        case 'pending':
          return 'bg-blue-50 border-blue-200';
        case 'removing':
          return 'bg-gray-100 border-gray-200';
        default:
          return 'border-gray-200';
      }
    },
    handleUnifiedRowClick(wrestler) {
      const state = this.getWrestlerRosterState(wrestler.id);
      switch (state) {
        case 'not_in_roster':
          this.addWrestler(wrestler);
          break;
        case 'active':
          this.removeFromRoster(wrestler.id);
          break;
        case 'pending':
          this.removePendingAddition(wrestler.id);
          break;
        case 'removing':
          this.undoRemoveByProfileId(wrestler.id);
          break;
      }
    },
    removeFromRoster(profileId) {
      if (this.pendingAdditions[profileId]) {
        this.removePendingAddition(profileId);
      } else if (this.currentMembers[profileId]) {
        this.$set(this.currentMembers[profileId], '_destroy', true);
        this.unsavedChanges = true;
      }
    },
    undoRemoveByProfileId(profileId) {
      if (this.currentMembers[profileId]) {
        this.$set(this.currentMembers[profileId], '_destroy', false);
        this.unsavedChanges = true;
      }
    },
    getMemberWeightClassId(profileId) {
      const member = this.pendingAdditions[profileId] || this.currentMembers[profileId];
      return member ? member.weight_class_id || '' : '';
    },
    handleUnifiedWeightClassChange(profileId, newWeightClassId) {
      const member = this.pendingAdditions[profileId] || this.currentMembers[profileId];
      if (!member) return;
      this.handleWeightClassChange(
        {
          profile: { id: profileId },
          _isPending: !!this.pendingAdditions[profileId],
          weight_class_id: member.weight_class_id,
        },
        newWeightClassId,
      );
    },
    addWrestler(wrestler) {
      const profileId = wrestler.id;

      // Check if this wrestler was previously removed from current members
      if (
        this.currentMembers[profileId]
        && this.currentMembers[profileId]._destroy
      ) {
        // Undo the removal instead of adding to pending
        this.currentMembers[profileId]._destroy = false;
        this.unsavedChanges = true;
        return;
      }

      // Check if already in roster (shouldn't happen but just in case)
      if (this.isWrestlerInRoster(profileId)) {
        return;
      }

      // Add to pending additions
      this.$set(this.pendingAdditions, profileId, {
        profile_id: profileId,
        profile_type: 'WrestlerProfile',
        weight_class_id: null,
        position: null,
        profile: {
          id: wrestler.id,
          full_name: wrestler.full_name,
          weight_class: wrestler.weight_class,
          academic_class: wrestler.academic_class,
          avatar_thumbnail: wrestler.avatar_thumbnail,
          dob: wrestler.dob,
          profile_type: wrestler.profile_type,
          gender: wrestler.gender,
        },
        _destroy: false,
      });
      this.unsavedChanges = true;
    },
    toggleMemberRemoval(member) {
      const profileId = member.profile.id;
      if (this.currentMembers[profileId]) {
        this.$set(this.currentMembers[profileId], '_destroy', true);
        this.unsavedChanges = true;
      }
    },
    removePendingAddition(profileId) {
      this.$delete(this.pendingAdditions, profileId);
      this.unsavedChanges = true;
    },
    tagCreated(tag) {
      this.showAddTagForm = false;
      this.tags.push(tag);
    },
    sessionSyncClicked(session) {
      this.$notificationManager.emit('profile-clicked');
      this.unsavedChanges = true;
      if (this.rosterSyncers[session.id]) {
        // If we already have a syncer check to see if we have an id
        const sync = this.rosterSyncers[session.id];
        if (sync.id) {
          // We have an existing syncer on the backend, so we toggle the destroy flag
          sync._destroy = !sync._destroy;
        } else {
          // We do not have an existing syncer on the backend, so we simply drop it from the payload
          this.$delete(this.rosterSyncers, session.id);
        }

        return;
      }

      // In this world we do not have a session id so we set it
      const syncer = {
        _destroy: false,
        paid_session: {
          id: session.id,
          name: session.name,
        },
      };
      this.$set(this.rosterSyncers, session.id, syncer);
    },
    tagSyncClicked(tag) {
      this.unsavedChanges = true;
      if (this.taggings[tag.id]) {
        // If we already have a tag, check to see if we have an id on it
        const tagging = this.taggings[tag.id];
        if (tagging.id) {
          // We have an existing tagging on the backend, so we toggle the destroy flag
          tagging._destroy = !tagging._destroy;
        } else {
          // We do not have an existing tagging on the backend, so we simply drop it from the payload
          this.$delete(this.taggings, tag.id);
        }

        return;
      }

      // In this world we do not have a tagging for this tag at all so we set it
      const tagging = {
        _destroy: false,
        tag,
      };
      this.$set(this.taggings, tag.id, tagging);
    },
    loadCurrentMembers() {
      const vm = this;
      vm.loadingCurrentMembers = true;

      const url = vm.$apiService.rosterMembershipsUrl(vm.existingRoster.id);
      vm.$apiService
        .loadAllPages(url, 'roster_memberships')
        .then((objects) => {
          // Build currentMembers keyed by profile_id
          const members = {};
          objects.forEach((membership) => {
            members[membership.profile.id] = {
              id: membership.id,
              profile_id: membership.profile.id,
              profile_type: membership.profile.type,
              weight_class_id: membership.weight_class_id,
              position: membership.position,
              profile: {
                id: membership.profile.id,
                // API returns display_name, not full_name
                full_name: membership.profile.display_name,
                weight_class: membership.profile.weight_class,
                avatar_thumbnail: membership.profile.avatar_thumbnail,
                dob: membership.profile.date_of_birth,
                profile_type: membership.profile.profile_type,
                academic_class: membership.profile.academic_class,
                gender: membership.profile.gender,
              },
              _destroy: false,
            };
          });
          vm.currentMembers = members;
          vm.loadingCurrentMembers = false;
        })
        .catch((error) => {
          vm.loadingCurrentMembers = false;
          vm.errorMessage = `Error retrieving roster members ${error.toString()}`;
          vm.error = true;
        });
    },
    loadTags() {
      const vm = this;
      const url = vm.$apiService.tagsUrl();
      vm.$apiService
        .loadAllPages(url, 'tags')
        .then((objects) => {
          vm.tags = objects;
        })
        .catch((error) => {
          vm.errorMessage = `Error retrieving tags ${error.toString()}`;
          vm.error = true;
        });
    },
    fetchDivisionStandards() {
      const vm = this;
      vm.loadingDivisionStandards = true;
      const url = vm.$apiService.divisionStandardsUrl();
      axios
        .get(url)
        .then((response) => {
          vm.divisionStandards = response.data.division_standards;
          vm.loadingDivisionStandards = false;
          // Initialize from existing roster after standards are loaded
          vm.initializeFromExistingAgeDivision();
          vm.ageDivisionRestored = true;
        })
        .catch((error) => {
          console.error('Error fetching division standards:', error);
          vm.loadingDivisionStandards = false;
        });
    },
    fetchLocations() {
      const vm = this;
      vm.loadingLocations = true;
      const url = `${vm.$apiService.locationsUrl()}?include_archived=true`;

      this.$apiService.loadAllPages(url, 'locations')
        .then((locations) => {
          vm.locations = locations;
          vm.loadingLocations = false;
        })
        .catch((error) => {
          console.error('Error fetching locations:', error);
          vm.loadingLocations = false;
        });
    },
    initializeFromExistingAgeDivision() {
      if (!this.existingRoster || !this.existingRoster.age_division_id) return;

      const targetAgeDivisionId = parseInt(this.existingRoster.age_division_id);

      // Find which division standard contains this age division
      for (const standard of this.divisionStandards) {
        const ageDivision = standard.age_divisions.find(
          (d) => d.id === targetAgeDivisionId,
        );
        if (ageDivision) {
          this.selectedDivisionStandardId = standard.id;
          this.selectedAgeDivisionId = ageDivision.id;
          // Expand all weight classes by default when editing
          for (const wc of ageDivision.weight_classes || []) {
            this.$set(this.expandedWeightClasses, wc.id, true);
          }
          break;
        }
      }
    },
    onDivisionStandardChange() {
      this.unsavedChanges = true;
      // When division standard changes, clear age division selection
      this.selectedAgeDivisionId = null;
      this.clearAllWeightClassAssignments();
    },
    onAgeDivisionChange() {
      const vm = this;
      this.unsavedChanges = true;
      // Check if we have any existing weight class assignments
      const hasAssignments = vm.allActiveMembers.some((m) => m.weight_class_id);

      if (hasAssignments) {
        swal
          .fire({
            icon: 'warning',
            title: 'Clear weight class assignments?',
            text: 'Changing the age division will clear all weight class assignments. Continue?',
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: 'Yes, change division',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary m-l-md',
              cancelButton: 'btn btn-default',
            },
          })
          .then((result) => {
            if (result.value) {
              vm.clearAllWeightClassAssignments();
              // Expand all weight classes for the new division
              vm.expandAllWeightClasses();
            } else {
              // Revert to previous selection (we don't track it, so just clear)
              // User cancelled, but we've already changed the value
            }
          });
      } else {
        // No assignments to clear, just expand weight classes
        this.expandAllWeightClasses();
      }
    },
    expandAllWeightClasses() {
      this.expandedWeightClasses = {};
      for (const wc of this.availableWeightClasses) {
        this.$set(this.expandedWeightClasses, wc.id, true);
      }
    },
    expandAllSections() {
      this.unassignedExpanded = true;
      this.expandAllWeightClasses();
    },
    collapseAllSections() {
      this.unassignedExpanded = false;
      this.expandedWeightClasses = {};
    },
    clearAllWeightClassAssignments() {
      // Clear assignments from current members
      for (const profileId of Object.keys(this.currentMembers)) {
        if (this.currentMembers[profileId]) {
          this.$set(this.currentMembers[profileId], 'weight_class_id', null);
          this.$set(this.currentMembers[profileId], 'position', null);
        }
      }
      // Clear assignments from pending additions
      for (const profileId of Object.keys(this.pendingAdditions)) {
        if (this.pendingAdditions[profileId]) {
          this.$set(this.pendingAdditions[profileId], 'weight_class_id', null);
          this.$set(this.pendingAdditions[profileId], 'position', null);
        }
      }
    },
    assignToWeightClass(member, weightClassId) {
      const profileId = member.profile.id;
      const targetStore = member._isPending
        ? this.pendingAdditions
        : this.currentMembers;

      if (!targetStore[profileId]) return;

      // Calculate next position for this weight class
      const existingInClass = this.allActiveMembers.filter(
        (m) => m.weight_class_id === weightClassId,
      );
      const nextPosition = existingInClass.length + 1;

      this.$set(targetStore[profileId], 'weight_class_id', weightClassId);
      this.$set(targetStore[profileId], 'position', nextPosition);
    },
    unassignFromWeightClass(member) {
      const profileId = member.profile.id;
      const targetStore = member._isPending
        ? this.pendingAdditions
        : this.currentMembers;

      if (!targetStore[profileId]) return;

      const oldWeightClassId = targetStore[profileId].weight_class_id;

      this.$set(targetStore[profileId], 'weight_class_id', null);
      this.$set(targetStore[profileId], 'position', null);

      // Reorder positions in the old weight class
      if (oldWeightClassId) {
        this.reorderPositions(oldWeightClassId);
      }
    },
    handleWeightClassChange(member, newWeightClassId) {
      const profileId = member.profile.id;
      const targetStore = member._isPending
        ? this.pendingAdditions
        : this.currentMembers;

      if (!targetStore[profileId]) return;
      this.unsavedChanges = true;

      const oldWeightClassId = targetStore[profileId].weight_class_id;

      if (newWeightClassId === null || newWeightClassId === '') {
        this.unassignFromWeightClass(member);
      } else {
        // Remove from old class first (to update positions)
        if (oldWeightClassId) {
          this.$set(targetStore[profileId], 'weight_class_id', null);
          this.$set(targetStore[profileId], 'position', null);
          this.reorderPositions(oldWeightClassId);
        }
        // Assign to new class
        this.assignToWeightClass(member, parseInt(newWeightClassId));
      }
    },
    handleWeightClassChangeAndClose(member, newWeightClassId) {
      this.handleWeightClassChange(member, newWeightClassId);
      this.editingWeightClassForMember = null;
    },
    reorderPositions(weightClassId) {
      // Get all members in this weight class, sorted by current position
      const members = this.allActiveMembers
        .filter((m) => m.weight_class_id === weightClassId)
        .sort((a, b) => (a.position || 999) - (b.position || 999));

      // Reassign positions 1, 2, 3...
      members.forEach((member, index) => {
        const profileId = member.profile.id;
        const targetStore = member._isPending
          ? this.pendingAdditions
          : this.currentMembers;
        if (targetStore[profileId]) {
          this.$set(targetStore[profileId], 'position', index + 1);
        }
      });
    },
    handleUnassignedDrop(newList) {
      this.unsavedChanges = true;
      // When wrestlers are dropped into unassigned, clear their weight_class_id
      newList.forEach((member) => {
        const profileId = member.profile.id;
        const targetStore = member._isPending
          ? this.pendingAdditions
          : this.currentMembers;
        if (targetStore[profileId]) {
          this.$set(targetStore[profileId], 'weight_class_id', null);
          this.$set(targetStore[profileId], 'position', null);
        }
      });
    },
    handleWeightClassDrop(weightClassId, newList) {
      this.unsavedChanges = true;
      // When wrestlers are dropped into a weight class, set their weight_class_id and position
      newList.forEach((member, index) => {
        const profileId = member.profile.id;
        const targetStore = member._isPending
          ? this.pendingAdditions
          : this.currentMembers;
        if (targetStore[profileId]) {
          this.$set(targetStore[profileId], 'weight_class_id', weightClassId);
          this.$set(targetStore[profileId], 'position', index + 1);
        }
      });
    },
    isWrestlerEligible(member) {
      if (!this.selectedAgeDivision) return true;

      const wrestler = {
        dob: member.profile?.dob,
        academic_class: member.profile?.academic_class,
        gender: member.profile?.gender,
      };
      const competitionYear = new Date().getFullYear();

      return isEligibleForDivision(
        wrestler,
        this.selectedAgeDivision,
        competitionYear,
      );
    },
    getWrestlerIneligibilityReason(member) {
      if (!this.selectedAgeDivision) return null;

      const wrestler = {
        dob: member.profile?.dob,
        academic_class: member.profile?.academic_class,
        gender: member.profile?.gender,
      };
      const competitionYear = new Date().getFullYear();

      return getIneligibilityReason(
        wrestler,
        this.selectedAgeDivision,
        competitionYear,
      );
    },
    handleWrestlerDetailsSaved(updatedData) {
      const { profileId } = updatedData;
      // Update the member's profile data in either currentMembers or pendingAdditions
      if (this.currentMembers[profileId]) {
        this.$set(
          this.currentMembers[profileId].profile,
          'weight_class',
          updatedData.weight_class,
        );
        this.$set(
          this.currentMembers[profileId].profile,
          'dob',
          updatedData.dob,
        );
        this.$set(
          this.currentMembers[profileId].profile,
          'gender',
          updatedData.gender,
        );
        this.$set(
          this.currentMembers[profileId].profile,
          'academic_class',
          updatedData.academic_class,
        );
      } else if (this.pendingAdditions[profileId]) {
        this.$set(
          this.pendingAdditions[profileId].profile,
          'weight_class',
          updatedData.weight_class,
        );
        this.$set(
          this.pendingAdditions[profileId].profile,
          'dob',
          updatedData.dob,
        );
        this.$set(
          this.pendingAdditions[profileId].profile,
          'gender',
          updatedData.gender,
        );
        this.$set(
          this.pendingAdditions[profileId].profile,
          'academic_class',
          updatedData.academic_class,
        );
      }
      // Close the inline edit form
      this.editingDetailsForMember = null;
    },
    toggleWeightClassExpanded(weightClassId) {
      this.$set(
        this.expandedWeightClasses,
        weightClassId,
        !this.expandedWeightClasses[weightClassId],
      );
    },
    jumpToSection(sectionId) {
      // Expand the section first
      if (sectionId === 'unassigned') {
        this.unassignedExpanded = true;
        this.$nextTick(() => {
          const el = this.$refs['unassigned-section'];
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      } else {
        // It's a weight class ID
        this.$set(this.expandedWeightClasses, sectionId, true);
        this.$nextTick(() => {
          const ref = this.$refs[`wc-section-${sectionId}`];
          const el = Array.isArray(ref) ? ref[0] : ref;
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    },
    getWeightClassCount(weightClassId) {
      return (this.wrestlersByWeightClass[weightClassId] || []).length;
    },
    memberMatchesSearch(member) {
      if (!this.memberSearchQuery.trim()) return true;
      const query = this.memberSearchQuery.toLowerCase().trim();
      const profile = member.profile || {};
      const fullName = (profile.full_name || '').toLowerCase();
      const firstName = (profile.first_name || '').toLowerCase();
      const lastName = (profile.last_name || '').toLowerCase();
      return (
        fullName.includes(query)
        || firstName.includes(query)
        || lastName.includes(query)
      );
    },
    formatWeightClassName(weightClass) {
      if (!weightClass) return '';
      return weightClass.display_name || `${weightClass.weight_value} lbs`;
    },
    getRosterMembershipParams() {
      const memberships = [];

      // Include ALL current members (for weight class updates, not just removals)
      for (const member of Object.values(this.currentMembers)) {
        if (member._destroy && member.id) {
          // Existing membership marked for removal
          memberships.push({
            id: member.id,
            profile_id: member.profile_id,
            profile_type: member.profile_type,
            _destroy: true,
          });
        } else if (member.id) {
          // Existing membership - include for weight class/position updates
          memberships.push({
            id: member.id,
            profile_id: member.profile_id,
            profile_type: member.profile_type,
            weight_class_id: member.weight_class_id,
            position: member.position,
          });
        }
      }

      // Include pending additions as new memberships
      for (const addition of Object.values(this.pendingAdditions)) {
        memberships.push({
          profile_id: addition.profile_id,
          profile_type: addition.profile_type,
          weight_class_id: addition.weight_class_id,
          position: addition.position,
        });
      }

      return memberships;
    },
    getSyncerParams() {
      return _.flatMap(this.rosterSyncers, (r) => {
        if (r.id) {
          return {
            id: r.id,
            paid_session_id: r.paid_session.id,
            _destroy: r._destroy,
          };
        }

        return {
          paid_session_id: r.paid_session.id,
          _destroy: r._destroy,
        };
      });
    },
    getTaggingsParams() {
      return _.flatMap(this.taggings, (tagging) => {
        if (tagging.id) {
          return {
            id: tagging.id,
            tag_id: tagging.tag.id,
            _destroy: tagging._destroy,
          };
        }

        return {
          tag_id: tagging.tag.id,
          _destroy: tagging._destroy,
        };
      });
    },
    // A save (e.g. the app's native Save) before division standards load must
    // not clear an existing roster's age division
    ageDivisionIdForSave() {
      if (this.existingRoster && !this.ageDivisionRestored) {
        return this.existingRoster.age_division_id || null;
      }
      return this.selectedAgeDivisionId;
    },
    buildParams() {
      return {
        roster: {
          name: this.name,
          age_division_id: this.ageDivisionIdForSave(),
          location_id: this.selectedLocationId,
          weight_tracking_disabled: this.weightTrackingDisabled,
          roster_memberships_attributes: this.getRosterMembershipParams(),
          roster_syncers_attributes: this.getSyncerParams(),
          taggings_attributes: this.getTaggingsParams(),
        },
      };
    },
    validate() {
      const vm = this;
      vm.error = false;

      if (vm.name.length === 0) {
        vm.error = true;
        vm.errorMessage = 'Please name the roster';
        vm.notifyAppOfValidationError(vm.errorMessage);
        return false;
      }

      return true;
    },
    // Vue watchers fire asynchronously, so flipping unsavedChanges right before
    // navigating leaves window.onbeforeunload set and triggers the "leave page?"
    // prompt. Clear it synchronously here, and keep `loading` true so a
    // cancelled prompt can't re-submit and create a duplicate.
    redirectTo(url) {
      this.unsavedChanges = false;
      window.onbeforeunload = null;
      window.location.assign(url);
    },
    // Every way out of the form after a write: the web lands on the roster's
    // wrestler list, the app embed pops back to native with `notification`.
    finish(url, notification) {
      if (this.forAppEmbed) {
        this.unsavedChanges = false;
        // Same as redirectTo: the unsavedChanges watcher runs too late, so
        // drop the native exit guard before asking the app to pop
        this.setNativeExitConfirmation(false, 'Discard roster changes?');
        this.askAppToPop(notification);
        return;
      }
      this.redirectTo(url);
    },
    rosterSavedNotification(rosterId) {
      return { name: 'roster_saved', data: { roster_id: rosterId } };
    },
    // Surface a failed write in the native UI as well as inline.
    showSaveError(message) {
      this.error = true;
      this.errorMessage = message;
      this.notifyAppOfValidationError(message);
    },
    saveRoster() {
      const vm = this;
      const valid = vm.validate();
      if (vm.loading || !valid) {
        return;
      }

      vm.loading = true;
      const params = vm.buildParams();
      if (vm.editing) {
        vm.updateRoster(params);
      } else {
        vm.createRoster(params);
      }
    },
    // TODO dry this up further
    createRoster(params) {
      const vm = this;
      const url = vm.$apiService.rostersUrl();
      axios
        .post(url, params)
        .then((response) => {
          vm.$notificationManager.emit('show-toast', 'Saved roster...', true);
          vm.finish(`/wrestlers?roster_ids[]=${response.data.id}`, vm.rosterSavedNotification(response.data.id));
        })
        .catch((error) => {
          vm.showSaveError(`Error creating roster ${error.toString()}`);
          vm.loading = false;
        });
    },
    updateRoster(params) {
      const vm = this;
      const url = vm.$apiService.rosterUrl(vm.existingRoster.id);
      axios
        .put(url, params)
        .then((response) => {
          vm.unsavedChanges = false;
          vm.$notificationManager.emit(
            'show-toast',
            'Saved roster. Processing updates...',
            true,
          );

          // Extract the batch ID from the response
          const batchId = response.data.batch_id;

          // Set up polling for batch status
          if (batchId) {
            vm.pollBatchStatus(batchId);
          } else {
            // Fallback if no batch ID is received
            vm.finish(`/wrestlers?roster_ids[]=${vm.existingRoster.id}`, vm.rosterSavedNotification(vm.existingRoster.id));
          }
        })
        .catch((error) => {
          vm.showSaveError(`Error updating roster ${error.toString()}`);
          vm.loading = false;
        });
    },
    pollBatchStatus(batchId) {
      const vm = this;
      const pollInterval = 500; // Poll every 500ms
      const batchStatusUrl = `/batch_status/${batchId}.json`;

      const checkStatus = () => {
        axios
          .get(batchStatusUrl)
          .then((response) => {
            const status = response.data;
            // Check if the batch is complete
            if (status.is_complete) {
              vm.$notificationManager.emit(
                'show-toast',
                'Roster update complete',
                true,
              );
              vm.finish(`/wrestlers?roster_ids[]=${vm.existingRoster.id}`, vm.rosterSavedNotification(vm.existingRoster.id));
            } else {
              // Continue polling
              setTimeout(checkStatus, pollInterval);
            }
          })
          .catch((error) => {
            vm.showSaveError(`Error checking batch status: ${error.toString()}`);
            vm.loading = false;
            vm.reSyncingRoster = false;
            // QQQ what should we do here? For now, nothing
            setTimeout(() => {
              console.log('Roster update failed');
              // window.location.assign(`/wrestlers?roster_ids[]=${vm.existingRoster.id}`);
            }, 2000);
          });
      };

      // Start polling
      checkStatus();
    },
    confirmDestroy() {
      const vm = this;
      swal
        .fire({
          icon: 'warning',
          title: `Are you sure you want to delete the ${vm.existingRoster.name} roster?`,
          text: 'You cannot undo deleting a roster.',
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes, delete roster',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger m-l-md',
            cancelButton: 'btn btn-default',
          },
        })
        .then((result) => {
          if (result.value) {
            axios
              .delete(vm.$apiService.rosterUrl(vm.existingRoster.id))
              .then((response) => {
                vm.$notificationManager.emit(
                  'show-toast',
                  'Roster deleted',
                  true,
                );
                vm.finish('/wrestlers', {
                  name: 'roster_deleted',
                  data: { roster_id: vm.existingRoster.id },
                });
              })
              .catch((error) => {
                vm.$notificationManager.emit(
                  'show-toast',
                  'Roster failed to delete',
                  false,
                );
                vm.notifyAppOfValidationError('Roster failed to delete');
              });
          }
        });
    },
    triggerSync() {
      if (this.reSyncingRoster) {
        return;
      }
      this.reSyncingRoster = true;
      const url = this.$apiService.rosterSyncUrl(this.existingRoster.id);
      axios
        .post(url)
        .then((response) => {
          this.$notificationManager.emit(
            'show-toast',
            'Processing roster re-sync...',
            true,
          );

          // Extract the batch ID from the response
          const batchId = response.data.batch_id;

          // Set up polling for batch status
          if (batchId) {
            this.pollBatchStatus(batchId);
          } else {
            // Fallback if no batch ID is received
            this.finish(`/wrestlers?roster_ids[]=${this.existingRoster.id}`, this.rosterSavedNotification(this.existingRoster.id));
          }
        })
        .catch((error) => {
          this.showSaveError(`Error re-syncing roster ${error.toString()}`);
          this.reSyncingRoster = false;
        });
    },
  },
};
</script>
